import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const JsonValidatorTool = () => {
  const [input, setInput] = useState('');
  const [isValid, setIsValid] = useState(null);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);

  const validate = () => {
    if (!input.trim()) {
      toast.error('Please enter JSON to validate');
      return;
    }

    try {
      const parsed = JSON.parse(input);
      setIsValid(true);
      setError('');
      
      // Calculate stats
      const countItems = (obj) => {
        if (Array.isArray(obj)) {
          return obj.reduce((acc, item) => acc + countItems(item), obj.length);
        } else if (typeof obj === 'object' && obj !== null) {
          return Object.keys(obj).reduce((acc, key) => acc + 1 + countItems(obj[key]), 0);
        }
        return 0;
      };

      const depth = (obj) => {
        if (typeof obj !== 'object' || obj === null) return 0;
        if (Array.isArray(obj)) {
          return obj.length === 0 ? 1 : 1 + Math.max(...obj.map(depth));
        }
        const values = Object.values(obj);
        return values.length === 0 ? 1 : 1 + Math.max(...values.map(depth));
      };

      setStats({
        type: Array.isArray(parsed) ? 'Array' : 'Object',
        items: countItems(parsed),
        depth: depth(parsed),
        size: new Blob([input]).size,
      });

      toast.success('Valid JSON!');
    } catch (e) {
      setIsValid(false);
      setError(e.message);
      setStats(null);
      toast.error('Invalid JSON');
    }
  };

  const copyInput = () => {
    navigator.clipboard.writeText(input);
    toast.success('Copied!');
  };

  return (
    <div className="space-y-4" data-testid="json-validator-tool">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">JSON Validator</h3>
        {input && (
          <Button variant="ghost" size="sm" onClick={copyInput}>
            <Copy className="w-4 h-4 mr-1" /> Copy
          </Button>
        )}
      </div>

      <Textarea
        placeholder='{"key": "value"}'
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setIsValid(null);
          setError('');
          setStats(null);
        }}
        className={`min-h-[200px] bg-background/50 font-mono text-sm ${
          isValid === true ? 'border-emerald-500' : 
          isValid === false ? 'border-red-500' : ''
        }`}
        data-testid="input"
      />

      <Button onClick={validate} className="w-full" data-testid="validate-btn">
        Validate JSON
      </Button>

      {isValid === true && (
        <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
          <div className="flex items-center gap-2 text-emerald-400 mb-3">
            <Check className="w-5 h-5" />
            <span className="font-semibold">Valid JSON</span>
          </div>
          {stats && (
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="p-2 rounded bg-background/50">
                <div className="text-sm font-bold">{stats.type}</div>
                <div className="text-xs text-muted-foreground">Type</div>
              </div>
              <div className="p-2 rounded bg-background/50">
                <div className="text-sm font-bold">{stats.items}</div>
                <div className="text-xs text-muted-foreground">Items</div>
              </div>
              <div className="p-2 rounded bg-background/50">
                <div className="text-sm font-bold">{stats.depth}</div>
                <div className="text-xs text-muted-foreground">Depth</div>
              </div>
              <div className="p-2 rounded bg-background/50">
                <div className="text-sm font-bold">{stats.size}B</div>
                <div className="text-xs text-muted-foreground">Size</div>
              </div>
            </div>
          )}
        </div>
      )}

      {isValid === false && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <div className="text-red-400 font-semibold mb-2">Invalid JSON</div>
          <div className="text-sm text-red-300 font-mono">{error}</div>
        </div>
      )}
    </div>
  );
};

export default JsonValidatorTool;
